/**
 * CMSPage - Content management surface.
 *
 * Tabs:
 *   - Checklists: reusable checklist templates with versioning and
 *     visibility scoping (companies / users / "all").
 *   - Photo Types: per-company photo taxonomy used by Work Order and
 *     Inspection photo uploads.
 *   - Areas: the inspection area vocabulary.
 *   - Business Lines: the business line / sub type taxonomy.
 *
 * Access: super admins see every tab. Admins (and developers) see
 * Checklists and Areas — the two surfaces the backend lets them write
 * (CMS_ROLES vs CMS_FULL_ROLES in utils/constants). Everyone else sees a
 * 403-style message instead of the CMS content. The route guard in App.tsx
 * already requires authentication; this component layers role
 * enforcement on top.
 *
 * This component is intentionally a thin shell — each tab body is its
 * own component to keep file sizes manageable as the surface grows.
 * The Checklist Editor is rendered as a full-page swap (controlled
 * by editingChecklist state) rather than a modal because the dynamic
 * items list and multi-select pivots benefit from the extra real estate.
 *
 * The swap unmounts the Checklists list, so the list's sub-tab and search
 * live here instead, where they survive the trip to the editor and back.
 * The sub-tab is also in the address (?type=inspection), so a refresh or
 * the browser's Back button lands on the same one. After a save the list
 * opens on the kind the template was saved as.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { TabBar } from '../../components/molecules';
import ChecklistsTab from './ChecklistsTab';
import PhotoTypesTab from './PhotoTypesTab';
import BusinessLinesTab from './BusinessLinesTab';
import AreasTab from './AreasTab';
import ChecklistEditor from './ChecklistEditor';
import type { ChecklistTemplateType } from '../../services/checklistTemplateService';
import { PageContainer } from '../../components/templates/PageContainer';
import { CMS_ROLES, CMS_FULL_ROLES } from '../../utils/constants';

/* Tab key union — extend here when adding more CMS surfaces */
type CMSTab = 'checklists' | 'photo_types' | 'areas' | 'business_lines';

/* Tab strip contents — keys must stay in sync with the CMSTab union above. */
/* Tabs open to admins as well as super admins — see CMS_FULL_ROLES. */
const ADMIN_CMS_TABS: CMSTab[] = ['checklists', 'areas'];

const CMS_TABS: { key: CMSTab; label: string }[] = [
    { key: 'checklists', label: 'Checklists' },
    { key: 'photo_types', label: 'Photo Types' },
    { key: 'areas', label: 'Areas' },
    { key: 'business_lines', label: 'Business Lines' },
];

/**
 * Editing state for the Checklist Editor:
 *   - null                      → not editing; show the tab list view
 *   - { mode: 'new', type }     → creating a brand-new template of that kind.
 *                                 The kind rides along because the Checklists
 *                                 tab is split per kind: creating from the
 *                                 Inspection sub-tab should open the editor
 *                                 already set to Inspection rather than making
 *                                 the author re-pick what they just clicked.
 *   - { id: string }            → editing an existing template (creates a new version on save)
 */
export type ChecklistEditTarget =
    | null
    | { mode: 'new'; type: ChecklistTemplateType }
    | { id: string };

const CMSPage: React.FC = () => {
    /* Auth — page-level role gate. The router only checks authentication,
     * not role membership, so we double-check here. */
    const { hasAnyRole } = useAuth();
    const canSeeAllTabs = hasAnyRole([...CMS_FULL_ROLES]);
    const visibleTabs = canSeeAllTabs ? CMS_TABS : CMS_TABS.filter((t) => ADMIN_CMS_TABS.includes(t.key));

    /* Active tab — defaults to checklists since that's the primary CMS surface */
    const [activeTab, setActiveTab] = useState<CMSTab>('checklists');

    /* Editor state — when non-null the editor takes over the whole page */
    const [editingChecklist, setEditingChecklist] = useState<ChecklistEditTarget>(null);

    /* Checklists sub-tab — kept in the address so it survives the editor
       swap, a refresh and the Back button. Work orders are the default and
       leave the address clean; only an inspection sub-tab adds ?type=. */
    const [searchParams, setSearchParams] = useSearchParams();
    const checklistType: ChecklistTemplateType = searchParams.get('type') === 'inspection' ? 'inspection' : 'work_order';
    const setChecklistType = (type: ChecklistTemplateType) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (type === 'inspection') next.set('type', type);
            else next.delete('type');
            return next;
        }, { replace: true });
    };

    /* Checklists search text — held here for the same reason, so a quick
       edit doesn't clear the filter. */
    const [checklistSearch, setChecklistSearch] = useState('');

    /* ---------------------------------------------------------------
     * Role gate — render a 403 view for roles outside CMS_ROLES
     * --------------------------------------------------------------- */
    if (!hasAnyRole([...CMS_ROLES])) {
        return (
            <div className="max-w-[640px] mx-auto mt-48px px-24px py-32px bg-white border border-brand-border rounded-md text-center">
                <h1 className="text-20px font-semibold text-brand-text mb-8px">Forbidden</h1>
                <p className="text-14px text-brand-text-muted">
                    The CMS is restricted to Admin and Super Admin users.
                </p>
            </div>
        );
    }

    /* ---------------------------------------------------------------
     * Editor mode — the editor takes over the page; back action returns to list
     * --------------------------------------------------------------- */
    if (editingChecklist !== null) {
        return (
            <ChecklistEditor
                target={editingChecklist}
                onClose={() => setEditingChecklist(null)}
                onSaved={(type) => {
                    setChecklistType(type);
                    setEditingChecklist(null);
                }}
            />
        );
    }

    /* ---------------------------------------------------------------
     * Default render — page header + tab nav + active tab body
     * --------------------------------------------------------------- */
    return (
        <PageContainer block="cms-page" dataRef="page-cms-page" width="wide">
            {/* Page header */}
            <div className="mb-24px">
                <h1 className="text-24px tablet:text-28px font-semibold text-brand-text">CMS</h1>
                <p className="text-14px text-brand-text-muted mt-4px">
                    {canSeeAllTabs
                        ? 'Manage checklist templates, photo types, inspection areas, and business lines.'
                        : 'Manage checklist templates and inspection areas.'}
                </p>
            </div>

            {/* Tab nav — shared TabBar molecule, same strip AdminPage uses */}
            <TabBar
                className="cms-page__tabs"
                tabs={visibleTabs}
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as CMSTab)}
            />

            {/* Active tab body */}
            {activeTab === 'checklists' && (
                <ChecklistsTab
                    onEdit={(id) => setEditingChecklist({ id })}
                    onCreate={(type) => setEditingChecklist({ mode: 'new', type })}
                    type={checklistType}
                    onTypeChange={setChecklistType}
                    search={checklistSearch}
                    onSearchChange={setChecklistSearch}
                />
            )}
            {activeTab === 'photo_types' && canSeeAllTabs && <PhotoTypesTab />}
            {activeTab === 'areas' && <AreasTab />}
            {activeTab === 'business_lines' && canSeeAllTabs && <BusinessLinesTab />}
        </PageContainer>
    );
};

export default CMSPage;
